import React from "react";
import { Link } from "react-router-dom";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import DescriptionIcon from "@mui/icons-material/Description";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PetsIcon from "@mui/icons-material/Pets";
import PlaceIcon from "@mui/icons-material/Place";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import ShieldIcon from "@mui/icons-material/Shield";
import SosIcon from "@mui/icons-material/Sos";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CarCrashIcon from "@mui/icons-material/CarCrash";
import CycloneIcon from "@mui/icons-material/Cyclone";
import FloodIcon from "@mui/icons-material/Flood";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import SignalCellularConnectedNoInternet0BarIcon from "@mui/icons-material/SignalCellularConnectedNoInternet0Bar";
import TornadoIcon from "@mui/icons-material/Tornado";
import TrialCta from "../../../../common/components/TrialCta";
import { scrollToId } from "../../Home/components/HomeHero";
import {
  BE_READY_BEFORE,
  BRIDGE_DISRUPTIONS,
  CAPABILITIES,
  FAMILY_QUESTIONS,
  FAMILY_QUESTIONS_SHORT,
  FINAL_ACTIONS,
  FINAL_UNKNOWNS,
  HERO_SCENARIOS,
  OFFLINE_CAPABILITY,
  OTHER_APPS,
  STAGES,
  TORNADO_SCENE,
  TRIAL_LABEL,
  VIDEOS,
} from "../data/mrfAppContent";
import "../styles/mrfApp.css";

// Emergency Alerts and Emergency Services Locations are listed per the 2026
// refresh brief. Offline access stays marked "In development" until testing
// confirms which modules are supported offline.

const ICONS = {
  accident: CarCrashIcon,
  alerts: NotificationsActiveIcon,
  block: BlockIcon,
  checkin: HowToRegIcon,
  checklist: ChecklistIcon,
  contacts: ContactPhoneIcon,
  evacuate: DirectionsRunIcon,
  flood: FloodIcon,
  hurricane: CycloneIcon,
  noSignal: SignalCellularConnectedNoInternet0BarIcon,
  tornado: TornadoIcon,
  unexpected: HelpOutlineIcon,
  wildfire: LocalFireDepartmentIcon,
  folderOff: FolderOffIcon,
  groups: GroupsIcon,
  hospital: LocalHospitalIcon,
  inventory: Inventory2Icon,
  medication: MedicationIcon,
  offline: CloudOffIcon,
  pets: PetsIcon,
  place: PlaceIcon,
  plan: EventNoteIcon,
  power: PowerOffIcon,
  sos: SosIcon,
  vault: DescriptionIcon,
};

const Icon = ({ name, className }) => {
  const Component = ICONS[name];
  return Component ? <Component className={className} aria-hidden="true" /> : null;
};

const Cta = (props) => <TrialCta liveLabel={TRIAL_LABEL} {...props} />;

const Video = ({ video }) => (
  <figure className="app-video">
    <div className="mrf-video">
      <iframe src={video.src} title={video.label} loading="lazy" allow="fullscreen; picture-in-picture" allowFullScreen />
    </div>
    <figcaption className="app-video__caption">{video.label}</figcaption>
  </figure>
);

const Hero = () => (
  <section className="app-hero" aria-labelledby="app-hero-heading">
    <div className="app-hero__wrap">
      <div className="app-hero__top">
      <div className="app-hero__copy">
        <p className="app-eyebrow app-eyebrow--on-dark">The MYREDFOLDER® App</p>
        <h1 id="app-hero-heading" className="app-hero__title">
          Your Family. Your Plan. Your MYREDFOLDER<sup>®</sup>.
        </h1>
        <p className="app-hero__sub">Emergencies don't happen according to plan. Your response can.</p>
        <p className="app-hero__text app-hero__text--strong">
          Prepare before the emergency. Stay connected when it matters. Have the information you need to begin
          recovering afterward.
        </p>
        <p className="app-hero__tagline">Be Ready Before You Need to Be.</p>
        <div className="app-actions">
          <Cta />
          <a className="app-hero__link" href="#see-it-in-action" onClick={(e) => scrollToId(e, "see-it-in-action")}>
            See MYREDFOLDER® in Action <span aria-hidden="true">↓</span>
          </a>
        </div>
      </div>

      <div className="app-hero__panel">
        <p className="app-hero__panel-label">Your family's plan, in one place</p>
        <p className="app-hero__text">
          In those moments, families shouldn't have to figure out what to do, where to go, how to reconnect, or where
          critical information is stored.
        </p>
        <p className="app-hero__text app-hero__text--last">
          MYREDFOLDER® puts your family's emergency plan, essential information, communication tools, important
          documents, checklists, and preparedness resources together in one secure, easy-to-use platform—so when
          something happens, <strong>your family already has a plan.</strong>
        </p>
      </div>
      </div>

      <div className="app-hero__dash">
        <p className="app-hero__dash-title">When this happens…</p>
        <ul className="app-hero__scenarios">
          {HERO_SCENARIOS.map((s) => (
            <li key={s.text}>
              <Icon name={s.icon} className="app-hero__scenario-icon" />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const PodcastSection = () => (
  <section
    id="see-it-in-action"
    tabIndex={-1}
    className="mrf-section"
    aria-labelledby="podcast-heading"
  >
    <div className="mrf-container app-feature">
      <Video video={VIDEOS.podcast} />
      <div className="app-feature__copy">
        <h2 id="podcast-heading" className="mrf-heading">The Emergency You Never Think Will Happen</h2>
        <p className="mrf-body app-body--ink">
          Most families know emergencies are possible. The challenge is believing one will happen today.
        </p>
        <p className="mrf-body">
          What happens when family members are in different places when an emergency begins? Who contacts whom? Where
          does everyone meet? How do you know who is safe? Where are your insurance documents, identification,
          medication information, emergency contacts, and other records if you suddenly have to leave home?
        </p>
        <p className="mrf-body">Those are difficult questions to answer in the middle of a crisis.</p>
        <p className="mrf-body app-body--ink">
          MYREDFOLDER® was created so families can answer them before the emergency begins.
        </p>
        <p className="mrf-body">
          MYREDFOLDER® transforms preparedness from something you intend to do someday into an organized, actionable
          family emergency plan. Build your household plan, establish meeting locations, organize emergency contacts,
          prepare for the hazards that affect you, securely maintain important information, and give your family a
          common place to turn when normal routines are disrupted.
        </p>
        <blockquote className="app-quote">
          <p>Because preparedness isn't about expecting the worst.</p>
          <p>It's about knowing what to do when the unexpected happens.</p>
        </blockquote>
      </div>
    </div>

    <div className="mrf-container">
      <div className="app-cta-band">
        <p className="app-cta-band__text">
          Don't wait for an emergency to discover what your family hasn't prepared for.
        </p>
        <Cta />
      </div>
    </div>
  </section>
);

const BridgeSection = () => (
  <section className="mrf-section mrf-section--alt" aria-labelledby="bridge-heading">
    <div className="mrf-container">
      <div className="app-intro">
        <h2 id="bridge-heading" className="mrf-heading mrf-heading--center">
          More Than an Emergency App. It's Your Family's Preparedness System.
        </h2>
        <p className="mrf-lead mrf-lead--center">Emergencies rarely affect just one part of life.</p>
      </div>
      <ul className="app-disruptions">
        {BRIDGE_DISRUPTIONS.map((d) => (
          <li key={d.text} className="app-disruptions__item">
            <Icon name={d.icon} className="app-disruptions__icon" />
            <span>{d.text}</span>
          </li>
        ))}
      </ul>
      <div className="app-intro">
        <p className="mrf-lead mrf-lead--center">
          That's why MYREDFOLDER® goes beyond a basic emergency notification app.
        </p>
        <p className="mrf-lead mrf-lead--center app-body--ink">
          It brings the critical pieces of preparedness, response, family coordination, information management, and
          recovery readiness into one connected platform.
        </p>
      </div>
    </div>
  </section>
);

const TornadoSection = () => (
  <section className="mrf-section" aria-labelledby="tornado-heading">
    <div className="mrf-container app-feature app-feature--reverse">
      <Video video={VIDEOS.tornado} />
      <div className="app-feature__copy">
        {/* <p className="app-eyebrow">Tornado scenario</p> */}
        <h2 id="tornado-heading" className="mrf-heading">Your Family May Be Apart When the Emergency Happens</h2>
        <ul className="app-scene">
          {TORNADO_SCENE.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="app-scene__alert">Then the warning arrives.</p>
        <p className="mrf-body">
          This is the reality of family emergency planning: the people you care about most may not be standing beside
          you when seconds matter.
        </p>
        <p className="mrf-body app-body--ink">MYREDFOLDER® helps families prepare for that reality.</p>
        <p className="mrf-body">
          Create your family Emergency Action Plan. Identify in-town and out-of-town meeting locations. Maintain
          emergency contacts. Establish work, school, and frequently visited locations. Keep family information
          organized. Use emergency communication and safety features to help your household coordinate when everyday
          routines suddenly disappear.
        </p>
      </div>
    </div>

    <div className="mrf-container">
      <div className="app-goal">
        <p className="app-goal__label">The goal is simple:</p>
        <p className="app-goal__lines">
          <span>Everyone knows the plan.</span> <span>Everyone knows where to go.</span>{" "}
          <span>Everyone knows how to reconnect.</span>
        </p>
        <p className="mrf-body app-goal__body">
          And instead of trying to create a plan while the emergency is unfolding, your family can focus on following
          the plan you already created.
        </p>
        <p className="app-goal__close">
          When Every Family Member Is Somewhere Different, Everyone Can Still Be Part of the Same Plan.
        </p>
        <Cta liveLabel="Build my family emergency plan" />
      </div>
    </div>
  </section>
);

const StagesSection = () => (
  <section className="mrf-section mrf-section--alt" aria-labelledby="stages-heading">
    <div className="mrf-container">
      <h2 id="stages-heading" className="mrf-heading mrf-heading--center">
        One MYREDFOLDER®. Before, During and After the Emergency.
      </h2>
      <ol className="app-stages">
        {STAGES.map((s) => (
          <li key={s.key} className={`app-stage app-stage--${s.key}`}>
            <p className="app-stage__label">{s.stage}</p>
            <h3 className="app-stage__title">{s.title}</h3>
            {s.lead && <p className="app-stage__lead">{s.lead}</p>}
            {s.items.length > 0 && (
              <ul className="app-checks">
                {s.items.map((item) => (
                  <li key={item}>
                    <CheckCircleIcon className="app-checks__icon" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.body.map((p) => (
              <p key={p} className="mrf-body">{p}</p>
            ))}
            <p className="app-stage__tagline">{s.tagline}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const BeReadySection = () => (
  <section className="mrf-section" aria-labelledby="ready-heading">
    <div className="mrf-container app-feature">
      <Video video={VIDEOS.beReady} />
      <div className="app-feature__copy">
        {/* <p className="app-eyebrow">Be ready</p> */}
        <h2 id="ready-heading" className="mrf-heading">The Best Time to Prepare Is Before the Alert Arrives</h2>
        <p className="mrf-body app-body--ink">
          There is a moment before every emergency when preparation is still possible.
        </p>
        <ul className="app-scene">
          {BE_READY_BEFORE.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="mrf-body">Before your family starts calling one another asking:</p>
        <ul className="app-bubbles">
          {FAMILY_QUESTIONS_SHORT.map((q) => (
            <li key={q} className="app-bubbles__item">“{q}”</li>
          ))}
        </ul>
        <p className="mrf-body app-body--ink">
          MYREDFOLDER® helps you answer those questions before they have to be asked.
        </p>
      </div>
    </div>
    <div className="mrf-container">
      <div className="app-unique">
        <p className="app-unique__title">Your family is unique. Your emergency plan should be too.</p>
        <p className="mrf-body">
          Build a preparedness plan around the people, places, pets, medications, documents, property, and
          circumstances that matter to your household.
        </p>
        <p className="mrf-body">Then, when circumstances change, update the plan.</p>
        <p className="app-unique__close">
          Preparedness isn't a document you create once and forget. <strong>It's a plan your family can actually use.</strong>
        </p>
      </div>
    </div>
  </section>
);

const CapabilitiesSection = () => (
  <section className="mrf-section mrf-section--alt" aria-labelledby="capabilities-heading">
    <div className="mrf-container">
      <h2 id="capabilities-heading" className="mrf-heading mrf-heading--center">
        Everything Your Family Needs to Become Better Prepared
      </h2>
      <ul className="app-capabilities">
        {CAPABILITIES.map((c) => (
          <li key={c.title} className="mrf-card app-capability">
            <span className="app-capability__icon">
              <Icon name={c.icon} />
            </span>
            <h3 className="app-capability__title">{c.title}</h3>
            <p className="mrf-card__body">{c.body}</p>
          </li>
        ))}
        <li className="mrf-card app-capability app-capability--wide">
          <span className="app-capability__icon">
            <Icon name={OFFLINE_CAPABILITY.icon} />
          </span>
          <div>
            <h3 className="app-capability__title">
              {OFFLINE_CAPABILITY.title} <span className="app-badge">{OFFLINE_CAPABILITY.status}</span>
            </h3>
            <p className="mrf-card__body">{OFFLINE_CAPABILITY.body}</p>
          </div>
        </li>
      </ul>
      <p className="app-center">
        <Cta />
      </p>
    </div>
  </section>
);

const DifferentiatorSection = () => (
  <section className="mrf-section app-different" aria-labelledby="different-heading">
    <div className="mrf-container">
      <h2 id="different-heading" className="mrf-heading mrf-heading--center app-different__heading">
        Why MYREDFOLDER® Is Different
      </h2>
      <div className="app-different__grid">
        <div>
          <ul className="app-others">
            {OTHER_APPS.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="app-different__claim">
            MYREDFOLDER® brings the pieces together around one thing that matters most: your family's ability to
            prepare, respond, reconnect, and recover.
          </p>
        </div>
        <div>
          <p className="app-different__start">It starts with your family.</p>
          <ul className="app-questions">
            {FAMILY_QUESTIONS.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
          <p className="app-different__close">MYREDFOLDER® helps turn those questions into a plan.</p>
        </div>
      </div>
    </div>
  </section>
);

const SecuritySection = () => (
  <section className="mrf-section" aria-labelledby="security-heading">
    <div className="mrf-container">
      <div className="app-security">
        <span className="app-security__icon" aria-hidden="true">
          <ShieldIcon />
        </span>
        <div>
          <h2 id="security-heading" className="mrf-heading">Your Family's Information Deserves Serious Protection</h2>
          <p className="mrf-body">
            Emergency preparedness can involve sensitive information. MYREDFOLDER® is being built with privacy and
            security incorporated into the platform's architecture, development, testing, and operational controls.
          </p>
          <p className="mrf-body">
            That includes safeguards for information in transit and at rest, access controls, secure authentication,
            security testing, privacy controls, and protections appropriate to the information being handled.
          </p>
          <p className="app-security__promise">
            Your family's emergency information should help protect your family—not become someone else's product.
          </p>
          <Link className="mrf-btn mrf-btn--secondary" to="/security">
            Learn about privacy &amp; security
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const FinalSection = () => (
  <section className="app-final" aria-labelledby="final-heading">
    <div className="mrf-container">
      <div className="app-final__top">
        <h2 id="final-heading" className="app-final__title">An Emergency Can Change Everything in Minutes.</h2>
        <div>
          <ul className="app-final__unknowns">
            {FINAL_UNKNOWNS.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="app-final__turn">But you can decide what happens before that moment arrives.</p>
        </div>
      </div>

      <ul className="app-final__actions">
        {FINAL_ACTIONS.map((line) => (
          <li key={line}>
            <CheckCircleIcon className="app-final__check" aria-hidden="true" />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <div className="app-final__close">
        <div className="app-final__gift">
          <p className="app-final__give">
            And give the people you care about something invaluable when circumstances become uncertain:
          </p>
          <p className="app-final__plan">A Plan.</p>
        </div>
        <div className="app-final__cta">
          <p className="app-final__brand">
            Your Family. Your Plan. Your MYREDFOLDER<sup>®</sup>.
          </p>
          <p className="app-final__start">Start preparing today—and be ready before you need to be.</p>
          <Cta className="mrf-btn mrf-btn--primary app-final__btn" />
          <p className="app-final__note">No waiting for the "right time." Your preparedness starts now.</p>
        </div>
      </div>
    </div>
  </section>
);

const MyRedFolder = () => (
  <div className="app-page">
    <Hero />
    <PodcastSection />
    <BridgeSection />
    <TornadoSection />
    <StagesSection />
    <BeReadySection />
    <CapabilitiesSection />
    <DifferentiatorSection />
    <SecuritySection />
    <FinalSection />
  </div>
);

export default MyRedFolder;
